import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SublyApi from '../../../helpers/Api';
import Card from '../../../components/ui/card';
import Label from '../../../components/ui/label';
import Button from '../../../components/ui/button';
import { FiSave, FiInfo } from 'react-icons/fi';

/**
 * AccessControlPanel - Manages form access control based on subscription tiers
 *
 * Allows admins to configure which subscription lookup keys can access each form.
 * Features:
 * - Universal Access toggle (available to all authenticated users)
 * - Multi-select checkboxes for subscription tiers
 * - Display tier name and lookup_key for each option
 * - Disable tier selection when Universal Access is enabled
 */
const AccessControlPanel = ({ formId, initialAccessSettings = {}, onSave, token }) => {
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState('');
	const [successMessage, setSuccessMessage] = useState('');

	// Available subscription lookup keys from the backend
	const [availableLookupKeys, setAvailableLookupKeys] = useState([]);

	// Access control state
	const [universalAccess, setUniversalAccess] = useState(
		initialAccessSettings.universalAccess || false
	);
	const [selectedLookupKeys, setSelectedLookupKeys] = useState(
		initialAccessSettings.allowedLookupKeys || []
	);

	// Load available lookup keys on mount
	useEffect(() => {
		const fetchLookupKeys = async () => {
			try {
				setLoading(true);
				setError('');

				const response = await SublyApi.request(
					'api/admin/subscription-lookup-keys',
					{},
					'get',
					{ Authorization: `Bearer ${token}` }
				);

				setAvailableLookupKeys(response.lookupKeys || []);
			} catch (err) {
				console.error('Error fetching subscription lookup keys:', err);
				setError('Failed to load subscription tiers. Please try again.');
			} finally {
				setLoading(false);
			}
		};

		if (token) {
			fetchLookupKeys();
		}
	}, [token]);

	// Load existing access settings when formId changes
	useEffect(() => {
		const fetchFormAccess = async () => {
			if (!formId || !token) return;

			try {
				setLoading(true);
				setError('');

				const response = await SublyApi.request(
					`api/admin/forms/${formId}/access`,
					{},
					'get',
					{ Authorization: `Bearer ${token}` }
				);

				setUniversalAccess(response.universalAccess || false);
				// Backend returns lookupKeys as array of objects, extract lookup_key strings
				const lookupKeyStrings = (response.lookupKeys || []).map(k => k.lookup_key);
				setSelectedLookupKeys(lookupKeyStrings);
			} catch (err) {
				console.error('Error fetching form access settings:', err);
				// Don't show error for new forms that haven't been saved yet
				if (err?.response?.status !== 404) {
					setError('Failed to load access settings. Please try again.');
				}
			} finally {
				setLoading(false);
			}
		};

		fetchFormAccess();
	}, [formId, token]);

	// Handle universal access toggle
	const handleUniversalAccessChange = (e) => {
		const checked = e.target.checked;
		setUniversalAccess(checked);

		// Clear selected lookup keys when universal access is enabled
		if (checked) {
			setSelectedLookupKeys([]);
		}
	};

	// Handle lookup key checkbox toggle
	const handleLookupKeyToggle = (lookupKey) => {
		setSelectedLookupKeys((prev) => {
			if (prev.includes(lookupKey)) {
				return prev.filter((key) => key !== lookupKey);
			} else {
				return [...prev, lookupKey];
			}
		});
	};

	// Handle save
	const handleSave = async () => {
		if (!formId) {
			setError('Form must be created before setting access controls.');
			return;
		}

		try {
			setSaving(true);
			setError('');
			setSuccessMessage('');

			// Convert lookup_key strings to IDs
			const lookupKeyIds = universalAccess
				? []
				: selectedLookupKeys
					.map(lookupKeyString => {
						const found = availableLookupKeys.find(k => k.lookup_key === lookupKeyString);
						return found ? found.id : null;
					})
					.filter(id => id !== null);

			const accessData = {
				universalAccess: universalAccess,
				lookupKeyIds: lookupKeyIds,
			};

			await SublyApi.request(
				`api/admin/forms/${formId}/access`,
				accessData,
				'post',
				{ Authorization: `Bearer ${token}` }
			);

			setSuccessMessage('Access control settings saved successfully!');

			// Scroll to top to show the success message
			window.scrollTo({ top: 0, behavior: 'smooth' });

			// Clear success message after 3 seconds
			setTimeout(() => setSuccessMessage(''), 3000);

			// Call parent onSave callback if provided
			if (onSave) {
				onSave({
					universalAccess,
					allowedLookupKeys: universalAccess ? [] : selectedLookupKeys,
				});
			}
		} catch (err) {
			console.error('Error saving access control settings:', err);
			setError(err.message || 'Failed to save access settings. Please try again.');

			// Scroll to top to show the error message
			window.scrollTo({ top: 0, behavior: 'smooth' });
		} finally {
			setSaving(false);
		}
	};

	if (loading) {
		return (
			<div className='flex justify-center items-center h-64'>
				<div className='animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900'></div>
			</div>
		);
	}

	return (
		<Card>
			<Card.Header>
				<h2 className='text-xl font-semibold font-glacial uppercase'>
					Access Control
				</h2>
				<p className='text-sm font-montserrat-regular text-gray-500'>
					Configure which subscription tiers can access this form
				</p>
			</Card.Header>
			<Card.Content>
				{/* Success Message */}
				{successMessage && (
					<div className='mb-6 p-4 bg-green-50 border border-green-200 rounded-md'>
						<p className='text-sm font-montserrat-medium text-green-800'>
							{successMessage}
						</p>
					</div>
				)}

				{/* Error Message */}
				{error && (
					<div className='mb-6 p-4 bg-red-50 border border-red-200 rounded-md'>
						<p className='text-sm font-montserrat-medium text-red-800'>{error}</p>
					</div>
				)}

				<div className='space-y-6'>
					{/* Universal Access Toggle */}
					<div className='pb-6 border-b border-gray-200'>
						<div className='flex items-start space-x-3'>
							<input
								type='checkbox'
								id='universal-access'
								checked={universalAccess}
								onChange={handleUniversalAccessChange}
								className='mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer'
							/>
							<div className='flex-1'>
								<Label
									htmlFor='universal-access'
									className='font-montserrat-medium text-base cursor-pointer'>
									Universal Access
								</Label>
								<p className='mt-1 text-sm font-montserrat-regular text-gray-500'>
									When enabled, all authenticated users can access this form
									regardless of their subscription tier.
								</p>
							</div>
						</div>
					</div>

					{/* Subscription Tier Selection */}
					<div>
						<div className='flex items-center space-x-2 mb-4'>
							<h3 className='text-base font-montserrat-medium text-gray-900'>
								Subscription Tiers
							</h3>
							{universalAccess && (
								<span className='text-xs font-montserrat-regular text-gray-500 bg-gray-100 px-2 py-1 rounded'>
									Disabled (Universal Access is on)
								</span>
							)}
						</div>

						{/* Helper Text */}
						<div className='mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md flex items-start space-x-2'>
							<FiInfo className='text-blue-600 mt-0.5 flex-shrink-0' />
							<p className='text-sm font-montserrat-regular text-blue-800'>
								{universalAccess
									? 'Universal Access is enabled. All users can access this form.'
									: selectedLookupKeys.length === 0
									? 'No subscription tiers selected. Only admins will be able to access this form.'
									: `Users with ${selectedLookupKeys.length} selected tier${
											selectedLookupKeys.length > 1 ? 's' : ''
									  } can access this form.`}
							</p>
						</div>

						{/* Lookup Key Checkboxes */}
						{availableLookupKeys.length === 0 ? (
							<div className='p-4 bg-gray-50 border border-gray-200 rounded-md text-center'>
								<p className='text-sm font-montserrat-regular text-gray-500'>
									No subscription tiers available. Please configure subscription
									products in Stripe.
								</p>
							</div>
						) : (
							<div className='space-y-3'>
								{availableLookupKeys.map((lookupKey) => (
									<div
										key={lookupKey.lookup_key}
										className={`flex items-start space-x-3 p-3 border rounded-md transition-colors ${
											universalAccess
												? 'bg-gray-50 border-gray-200'
												: selectedLookupKeys.includes(lookupKey.lookup_key)
												? 'bg-primary/5 border-primary/30'
												: 'bg-white border-gray-200 hover:border-gray-300'
										}`}>
										<input
											type='checkbox'
											id={`lookup-key-${lookupKey.lookup_key}`}
											checked={selectedLookupKeys.includes(lookupKey.lookup_key)}
											onChange={() =>
												handleLookupKeyToggle(lookupKey.lookup_key)
											}
											disabled={universalAccess}
											className='mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer disabled:cursor-not-allowed disabled:opacity-50'
										/>
										<div className='flex-1'>
											<Label
												htmlFor={`lookup-key-${lookupKey.lookup_key}`}
												className={`font-montserrat-medium text-sm ${
													universalAccess
														? 'cursor-not-allowed opacity-50'
														: 'cursor-pointer'
												}`}>
												{lookupKey.name || lookupKey.lookup_key}
											</Label>
											<p
												className={`mt-0.5 text-xs font-montserrat-regular ${
													universalAccess ? 'text-gray-400' : 'text-gray-500'
												}`}>
												Lookup Key: {lookupKey.lookup_key}
											</p>
										</div>
									</div>
								))}
							</div>
						)}
					</div>

					{/* Save Button */}
					<div className='pt-6 border-t border-gray-200 flex items-center justify-between'>
						<p className='text-sm font-montserrat-regular text-gray-500'>
							{formId
								? 'Changes will be saved immediately'
								: 'Please create the form before configuring access controls'}
						</p>
						<Button
							onClick={handleSave}
							disabled={!formId || saving}
							className='bg-clientBlack hover:bg-clientNero text-white font-montserrat-medium'>
							{saving ? (
								<>
									<div className='animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2'></div>
									Saving...
								</>
							) : (
								<>
									<FiSave className='h-4 w-4 mr-2' />
									Save Access Settings
								</>
							)}
						</Button>
					</div>
				</div>
			</Card.Content>
		</Card>
	);
};

AccessControlPanel.propTypes = {
	formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	initialAccessSettings: PropTypes.shape({
		universalAccess: PropTypes.bool,
		allowedLookupKeys: PropTypes.arrayOf(PropTypes.string),
	}),
	onSave: PropTypes.func,
	token: PropTypes.string.isRequired,
};

export default AccessControlPanel;
